/**
 * 주간 출석 리스트 — 한 주(일~토)를 세로로 나열
 * 각 날짜에 그 날 학생별 출석 상태를 전부 표시
 * value: 'YYYY-MM-DD' / onChange / weekData: { [date]: records } / students: [{id,name}]
 */
import React from 'react';
import styled from 'styled-components';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

const WEEK = ['일', '월', '화', '수', '목', '금', '토'];
const pad = (n) => String(n).padStart(2, '0');
const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const normStatus = (v) => (!v ? '' : (typeof v === 'string' ? v : v.status || ''));

const STATUS = {
  present: { label: '출석', color: '#4A7CFE' },
  absent: { label: '결석', color: '#EF4444' },
  late: { label: '지각', color: '#F59E0B' },
  excused: { label: '사유', color: '#9CA3AF' },
};

const WeekList = ({ value, onChange, weekData = {}, students = [] }) => {
  const base = value ? new Date(value) : new Date();
  const sunday = new Date(base);
  sunday.setDate(base.getDate() - base.getDay());

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    return d;
  });

  const moveWeek = (delta) => {
    const cur = value ? new Date(value) : new Date();
    cur.setDate(cur.getDate() + delta * 7);
    onChange(toStr(cur));
  };

  const todayStr = toStr(new Date());
  const last = days[6];
  const label = `${sunday.getMonth() + 1}.${sunday.getDate()} ~ ${last.getMonth() + 1}.${last.getDate()}`;

  return (
    <Wrap>
      <Head>
        <Nav type="button" onClick={() => moveWeek(-1)}><IoChevronBack size={18} /></Nav>
        <Label>{label}</Label>
        <Nav type="button" onClick={() => moveWeek(1)}><IoChevronForward size={18} /></Nav>
      </Head>
      {days.map((d, i) => {
        const ds = toStr(d);
        const rec = weekData[ds];
        const entries = rec
          ? Object.entries(rec).filter(([, v]) => normStatus(v))
          : [];
        const summary = (() => {
          if (entries.length === 0) return null;
          const counts = {};
          entries.forEach(([, v]) => {
            const s = normStatus(v);
            counts[s] = (counts[s] || 0) + 1;
          });
          const parts = [];
          ['present', 'late', 'absent', 'excused'].forEach((k) => {
            if (counts[k]) parts.push(`${STATUS[k].label} ${counts[k]}`);
          });
          return parts.join(' · ');
        })();
        return (
          <DayRow
            key={ds}
            type="button"
            $on={ds === value}
            onClick={() => onChange(ds)}
          >
            <DayTop>
              <Dow $i={i}>{WEEK[i]}</Dow>
              <DayNum $on={ds === value} $today={ds === todayStr}>{d.getDate()}</DayNum>
              {entries.length === 0 && <None>미체크</None>}
              {entries.length > 0 && <Summary>{summary}</Summary>}
            </DayTop>
          </DayRow>
        );
      })}
    </Wrap>
  );
};

export default WeekList;

const Wrap = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  padding: 6px;
  margin-bottom: 12px;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 8px 0 10px;
`;

const Nav = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Label = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DayRow = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px 10px;
  border-radius: 10px;
  background: ${({ $on, theme }) => ($on ? theme.colors.primaryLight : 'transparent')};
  text-align: left;
  & + & { border-top: 1px solid #F0F1F4; }
`;

const DayTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Dow = styled.span`
  width: 18px;
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ $i }) => {
    if ($i === 0) return '#EF4444';
    if ($i === 6) return '#4A7CFE';
    return '#6B7280';
  }};
`;

const DayNum = styled.span`
  font-size: 15px;
  font-weight: ${({ $on, $today }) => ($on || $today ? 800 : 600)};
  color: ${({ $on, $today, theme }) =>
    ($on || $today ? theme.colors.primary : theme.colors.text.primary)};
`;

const None = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const Summary = styled.span`
  margin-left: auto;
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

